import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateAwardSimpleDetailsMutation } from '../../hooks'

const AwardSimpleDetailsForm = ({
  id,
  name,
  description,
  position,
  maxPosition,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.AWARD.UPDATE_DETAILS,
  ])

  const [updateAwardDetails, { isLoading: isSaveLoading, error: saveError }] =
    useUpdateAwardSimpleDetailsMutation()

  const initialValues = useMemo(
    () => ({ description, position }),
    [description, position],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateAwardDetails(id, data),
    [id, updateAwardDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Award details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [description, position, resetForm])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editAward"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.AWARD.UPDATE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

AwardSimpleDetailsForm.propTypes = {
  id: idPropType.isRequired,
  description: PropTypes.string,
  name: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  maxPosition: PropTypes.number.isRequired,
}

AwardSimpleDetailsForm.defaultProps = {
  description: undefined,
}

export default AwardSimpleDetailsForm
